import { definePage, type PluginWebManifest } from '@reflex/plugin-api/web';

const page = definePage({
  path: '/configure/connect/mcp',
  label: 'MCP Servers',
  icon: 'Plug',
  group: 'security',
  componentPath: '@reflex/plugin-mcp/web/McpServersPage',
  description: 'Give agents external tools through Model Context Protocol servers.',
  details:
    'Pick popular servers (GitHub, Linear, Notion, Sentry, Honeycomb, …) from a built-in catalog and connect them with OAuth — each member authorizes their own account, like in Cursor or Claude — or register custom remote (HTTP) and local (stdio subprocess) servers. Stored credentials stay server-side. The host probes new servers on save to discover their tool list.',
  features: [
    'Catalog of popular servers with one-click OAuth connect',
    'Per-user OAuth connections with automatic token refresh',
    'Per-org registry of remote and local MCP servers',
    'Local stdio servers run as a subprocess in each agent devbox',
    'Stored bearer tokens stay server-side',
    'Attach MCP servers to an agent run from the new-agent picker',
    'MCP Servers section on every agent detail page',
    "Built-in probe to discover a server's tool list before saving",
  ],
});

export const mcpWeb: PluginWebManifest = {
  ...page.web,
  // The standalone /mcp-servers route is the daily-use surface
  // (browse + manage MCP servers). The same component is also
  // embedded inside the wizard slide-over and the plugin detail
  // page's Setup section so initial setup happens in-place during
  // onboarding without forcing a navigation. NavItem stays at
  // /mcp-servers — users access this list frequently after setup.
  orgSetupComponent: {
    componentPath: '@reflex/plugin-mcp/web/McpServersPage',
    label: 'Register an MCP server so agents can call external tools. Optional.',
  },
  attachments: [
    {
      id: 'mcp-servers',
      label: 'MCP Servers',
      icon: 'Plug',
      order: 20,
      pickerComponentPath: '@reflex/plugin-mcp/web/McpAttachmentPicker',
      summaryComponentPath: '@reflex/plugin-mcp/web/McpAttachmentSummary',
      summaryIncludesIcon: true,
    },
  ],
  launchMentionProviders: [
    {
      id: 'launch-mention-mcp',
      label: 'MCP Servers',
      keyword: 'mcp',
      icon: 'Plug',
      componentPath: '@reflex/plugin-mcp/web/McpMentionProvider',
      createComponentPath: '@reflex/plugin-mcp/web/McpMentionCreate',
      createLabel: 'Add MCP server',
      // Remote MCP servers can also be attached to a running session: the
      // server mints live MCP hub tokens for the devbox and registers the
      // server in the agent's native config on the spot.
      runtimeAttachable: true,
      order: 30,
    },
  ],
  agentDetailSections: [
    {
      id: 'mcp-servers',
      label: 'MCP Servers',
      icon: 'Plug',
      componentPath: '@reflex/plugin-mcp/web/McpDetailSection',
      order: 10,
    },
  ],
  resourcePreviews: [
    {
      kind: 'mcp',
      label: 'MCP Server',
      icon: 'Plug',
      componentPath: '@reflex/plugin-mcp/web/McpPreview',
      detailHref: '/configure/connect/mcp',
      // `mcp` is the config prefix (`shared/ids.ts`); a bare `mcp_…` id
      // anywhere in the app resolves this preview's icon and hover card.
      // `mco` (OAuth connection) is deliberately absent — the preview
      // renders configs, and a connection id would only say "not found".
      idPrefixes: ['mcp'],
    },
  ],
  marketingPreviews: [
    {
      id: 'mcp-servers-panel',
      label: 'MCP servers panel',
      description:
        'Sample MCP server registry showing remote and local servers with their tool lists.',
      surface: 'panel',
      componentPath: '@reflex/plugin-mcp/web/McpServersPagePreview',
    },
  ],
};
